import styles from "./styles.module.scss";
import { DataTable } from "../../components/tables";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RelativeComponentLoader } from "../../components/loader";
import { Paginate } from "../../components/paginate";
import { ColorInsightTags, ColorTag, FilterTagWrapper } from "../../components/tags";
import { Navigate, useLocation, useParams, Outlet, Link } from "react-router-dom";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import usePaginate from "@/components/paginate/usePaginate";
import {
  compareSnowflakeJobRuns,
  getSnowflakeJobDefinition,
  getSnowflakeJobFilters,
  getSnowflakeJobGraphData,
  getWorkloadAttributeValues,
  getWorkloadAttributes,
  getSnowflakeJobRuns,
  getSnowflakeJobRunTasks,
  getSnowflakeJobs,
  getSnowflakeJobsSummary,
  getSnowflakeJobGraphSummary,
} from "@/helpers/apis";
import {
  appsFilter as appsFilterConfig,
  teamsFilter as teamsFilterConfig,
  workflowIdsFilter,
} from "@/pages/query/utils/snowflakeJobsFilterConfig";
import { formatCost, formatMilliseconds, formatBytes } from "@/helpers/utils";
import { useAppState } from "@/modules/app/useAppContext";
import classNames from "classnames";
import { Chart, ChartTypes } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import { Bar, Rectangle, TooltipProps } from "recharts";
import dayjs from "dayjs";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { DateRange } from "@/components/dateRange";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { getFilterStringByMode } from "@/components/tags/utils";
import {
  GetSnowflakeJobItem,
  GetSnowflakeJobRunItem,
  GetSnowflakeJobsParams,
  GetSnowflakeJobTaskItem,
  RunCompareDiff,
  SnowflakeJobDefinition,
  WorkflowType,
} from "./types";
import QueryTextWithHover from "./QueryText";
import CompareAccordionContent from "./CompareAccordionContent";
import { useQuery, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { WarehouseSizeMap } from "../warehouse/components/scheduler/helper";
import { Drawer } from "@/uiCore";
import { DrawerRef } from "@/uiCore/components/drawer";
import {
  Button,
  Card,
  Combobox,
  ComboboxOption,
  Label,
  NativeSelect,
  Typography,
} from "@altimateai/lego";
import { GroupByDropdown } from "./components/GroupByDropdown";
import { ExecutionStatus, ExecutionStatusCount } from "@/pages/query/ExecutionStatusCount";
import { CostChartTooltip, PercentChange } from "@/pages/query/Graph";
import {
  AGGREGATE_BY_OPTIONS,
  createColorMapFromObjectKeys,
  getPercentKey,
  OTHER,
} from "@/pages/query/common";
import {
  getWorkflowNavigationPrefix,
  getWorkflowTerminology,
  getWorkflowIdLabel,
} from "@/pages/query/snowflake";
import { WorkflowTitle } from "@/pages/query/snowflake";
import { WorkflowIdCell } from "@/pages/query/snowflake";
import SpacesRedirectionButton from "@/components/spaces/SpacesRedirectionButton";
import { SPACES_TAG_MAPPING } from "@/helpers/spacesConstants";
import { IconArrowLeft, IconArrowsLeftRight } from "@tabler/icons-react";
import { QueriesTracking } from "@/lib/tracking/queries";
import { FilterType, TableHeaderType, parseSortDirection } from "@/components/tables/types";
import { TotalCostHeader } from "@/pages/query/common/TotalCostHeader";
import { FilterTextChangeValue } from "@/components/filterDropdown";

const SnowflakeJobList = ({ workflowType }: { workflowType: WorkflowType }) => {
  const { searchParams, deleteSearchParamsByKeyValue, updateSearchParamsByKey } =
    useFiltersBySearchParams();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();

  const { currency, currencySymbol } = useAppState();

  const sortAttribute = searchParams.get("sortAttribute") || "query_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");

  const costFilter = searchParams.get("query_cost");
  const executionTimeFilter = searchParams.get("executionTime");
  const workflowFilter = searchParams.getAll("workflows") || [];
  const warehouseFilter = searchParams.getAll("warehouses") || [];
  const teamsFilter = searchParams.getAll("teams") || [];
  const appsFilter = searchParams.getAll("apps") || [];

  const executionTimeFilterMode = searchParams.get("executionTimeFilterMode") || "gt";
  const costFilterMode = searchParams.get("costFilterMode") || "gt";

  const [aggregateBy, setAggregateBy] = useState<string>("project_id");
  const [selectedLegends, setSelectedLegends] = useState<string[]>([]);
  const [allLegends, setAllLegends] = useState<string[]>([]);

  const { startDate, endDate, onUpdate } = useDateFromSearchParams();

  // Query parameters
  const getFilterParams = () => {
    const params: Partial<GetSnowflakeJobsParams> = {
      page: page + 1,
      size: pageSize,
      sortAttribute,
      sortOrder,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      workflows: workflowFilter,
      warehouses: warehouseFilter,
      teams: teamsFilter,
      apps: appsFilter,
      workflow_type: workflowType,
    };

    if (costFilter) {
      params.costFilter = costFilter;
      params.costFilterMode = costFilterMode === "lt" ? "lt" : "gt";
    }

    if (executionTimeFilter) {
      params.executionTimeFilter = parseFloat(executionTimeFilter) * 1000;
      params.executionTimeFilterMode = executionTimeFilterMode === "lt" ? "lt" : "gt";
    }

    return params;
  };

  // Fetch job data using OpenSearch query
  const { data = { items: [], total: 0, pages: 0, page: 0, size: 0 }, isLoading } = useQuery({
    queryKey: [
      "snowflake_jobs",
      page,
      pageSize,
      sortAttribute,
      sortOrder,
      costFilter,
      costFilterMode,
      ...workflowFilter,
      ...warehouseFilter,
      ...teamsFilter,
      ...appsFilter,
      executionTimeFilter,
      executionTimeFilterMode,
      startDate,
      endDate,
      workflowType,
    ],
    queryFn: () => getSnowflakeJobs(getFilterParams()),
  });

  const { data: filters, isFetching: isFiltersFetching } = useQuery({
    queryKey: ["snowflake_jobs", "filters", startDate, endDate, workflowType],
    queryFn: () =>
      getSnowflakeJobFilters({
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workflow_type: workflowType,
      }),
  });

  // TODO(AI-6471): cache {value, label} pairs from the workflow_ids
  // API_DROPDOWN onChange so the chip below can render tableau dashboard
  // names instead of raw IDs.

  // Fetch workload attributes from global_config (needed for isCustomTagSelected check)
  const { data: groupByOptionsData } = useQuery({
    queryKey: ["workload", "attributes"],
    queryFn: getWorkloadAttributes,
  });

  // Custom tags don't apply to TABLEAU — strip them from the GroupByDropdown.
  const groupByOptions = useMemo(() => {
    if (!groupByOptionsData?.options) return undefined;
    if (workflowType === WorkflowType.TABLEAU) {
      return groupByOptionsData.options.filter(o => !o.is_custom);
    }
    return groupByOptionsData.options;
  }, [groupByOptionsData?.options, workflowType]);

  // Check if the currently selected aggregateBy is a custom tag
  // Use useMemo to ensure stable calculation
  const isCustomTagSelected = useMemo(() => {
    if (!groupByOptionsData?.options) return false;
    const option = groupByOptionsData.options.find(opt => opt.key === aggregateBy);
    return option?.is_custom ?? false;
  }, [groupByOptionsData?.options, aggregateBy]);

  // Check if project_id or app_id is selected (built-in group-by options)
  const isProjectSelected = aggregateBy === "project_id";
  const isAppSelected = aggregateBy === "app_id";

  // Selected filter values for built-in group-by options (project/app)
  const [selectedBuiltInFilters, setSelectedBuiltInFilters] = useState<string[]>([]);

  // Compute the effective teams/apps filters for the graph data API
  // When project_id is selected as group-by, use selectedBuiltInFilters as teams filter
  // When app_id is selected as group-by, use selectedBuiltInFilters as apps filter
  const graphTeamsFilter = useMemo(() => {
    if (isProjectSelected && selectedBuiltInFilters.length > 0) {
      return selectedBuiltInFilters;
    }
    return teamsFilter;
  }, [isProjectSelected, selectedBuiltInFilters, teamsFilter]);

  const graphAppsFilter = useMemo(() => {
    if (isAppSelected && selectedBuiltInFilters.length > 0) {
      return selectedBuiltInFilters;
    }
    return appsFilter;
  }, [isAppSelected, selectedBuiltInFilters, appsFilter]);

  const graphParams = {
    start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
    end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
    workflows: workflowFilter,
    warehouses: warehouseFilter,
    teams: graphTeamsFilter,
    apps: graphAppsFilter,
    aggregate_by: aggregateBy,
    workflow_type: workflowType,
  };

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: [
      "snowflake_jobs",
      "graph_data",
      aggregateBy,
      startDate,
      endDate,
      ...workflowFilter,
      ...warehouseFilter,
      ...graphTeamsFilter,
      ...graphAppsFilter,
      workflowType,
      ...selectedBuiltInFilters,
    ],
    queryFn: () => getSnowflakeJobGraphData(graphParams),
  });

  const graphSummaryQueryKey = [
    "snowflake_jobs",
    "graph_data_summary",
    aggregateBy,
    startDate,
    endDate,
    ...workflowFilter,
    ...warehouseFilter,
    ...graphTeamsFilter,
    ...graphAppsFilter,
    workflowType,
    ...selectedBuiltInFilters,
  ];

  // Fetch entities based on selected workload attribute (only for custom tags)
  const [entitySearchTerm, setEntitySearchTerm] = useState("");
  const entitySearchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: groupByEntitiesData,
    fetchNextPage: fetchNextEntityPage,
    hasNextPage: hasMoreEntities,
    isFetchingNextPage: isFetchingNextEntityPage,
    refetch: refetchEntities,
  } = useInfiniteQuery({
    queryKey: [
      "workload",
      "attribute_values",
      aggregateBy,
      startDate,
      endDate,
      workflowType,
      entitySearchTerm,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      getWorkloadAttributeValues({
        attribute_key: aggregateBy,
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() || "",
        workflow_type: workflowType,
        page: pageParam,
        size: 50,
        search: entitySearchTerm || undefined,
      }),
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined;
      return lastPage.page < Math.ceil(lastPage.total / lastPage.size)
        ? lastPage.page + 1
        : undefined;
    },
    enabled: !!aggregateBy && isCustomTagSelected,
    placeholderData: keepPreviousData,
  });

  // Flatten paginated entity values
  const entityValues = useMemo(() => {
    if (!groupByEntitiesData?.pages) return [];
    return groupByEntitiesData.pages.flatMap(page => page?.values ?? []);
  }, [groupByEntitiesData]);

  // Entity search handler with debounce
  const handleEntitySearch = useCallback((term: string) => {
    if (entitySearchDebounceRef.current) {
      clearTimeout(entitySearchDebounceRef.current);
    }
    entitySearchDebounceRef.current = setTimeout(() => {
      setEntitySearchTerm(term);
    }, 300);
  }, []);

  // Entity load more handler
  const handleEntityLoadMore = useCallback(
    async (searchValue?: string, _requestedPage?: number) => {
      if (typeof searchValue === "string") {
        handleEntitySearch(searchValue);
        return;
      }
      if (hasMoreEntities && !isFetchingNextEntityPage) {
        await fetchNextEntityPage();
      }
    },
    [hasMoreEntities, isFetchingNextEntityPage, fetchNextEntityPage, handleEntitySearch]
  );

  // Reset entity search when filter changes
  const handleEntityPageReset = useCallback(() => {
    setEntitySearchTerm("");
    refetchEntities();
  }, [refetchEntities]);

  // Get filter options for built-in group-by (project/app) from the filters API
  const builtInFilterOptions = useMemo(() => {
    if (!filters) return [];
    if (isProjectSelected) {
      return filters.teams.map(t => ({ label: t, value: t }));
    }
    if (isAppSelected) {
      return filters.apps.map(a => ({ label: a, value: a }));
    }
    return [];
  }, [filters, isProjectSelected, isAppSelected]);

  // Reset selected built-in filters when aggregateBy changes
  useEffect(() => {
    setSelectedBuiltInFilters([]);
  }, [aggregateBy]);

  // Compute legends from graph data for non-custom tags, or from entities for custom tags
  const computedLegends = useMemo(() => {
    // For custom tags, use entities from the paginated API
    if (isCustomTagSelected && entityValues.length > 0) {
      if (entityValues.length > 10) {
        return {
          all: entityValues,
          selected: [OTHER, ...entityValues.slice(0, 10)],
        };
      }
      return {
        all: entityValues,
        selected: entityValues,
      };
    }

    // For non-custom tags (project_id, app_id), compute from graph data
    if (!isCustomTagSelected && graphData) {
      const totalCost: Record<string, number> = {};

      for (const item of graphData) {
        for (const key in item) {
          if (key === "date") continue;

          const nestedObject = item[key];
          const identifier =
            nestedObject?.dbt_project_name === null ? key : nestedObject?.dbt_project_name;
          if (!identifier) continue;

          totalCost[identifier] = totalCost[identifier] || 0;
          totalCost[identifier] += nestedObject?.cost_value || 0;
        }
      }

      const totalCostItems = Object.entries(totalCost);
      totalCostItems.sort((a, b) => b[1] - a[1]);

      const all = totalCostItems.map(x => x[0]);
      const selected = (totalCostItems.length > 10 ? [OTHER] : []).concat(
        totalCostItems.slice(0, 10).map(x => x[0])
      );

      return { all, selected };
    }

    return { all: [], selected: [] };
  }, [isCustomTagSelected, entityValues, graphData]);

  // Update state when computed legends change
  useEffect(() => {
    if (computedLegends.all.length > 0) {
      setAllLegends(computedLegends.all);
      setSelectedLegends(computedLegends.selected);
    }
  }, [computedLegends]);

  const handleCostFilterChange = ({ value, mode }: FilterTextChangeValue) => {
    updateSearchParamsByKey({
      query_cost: value,
      costFilterMode: mode,
    });
  };

  const handleExecutionTimeFilterChange = ({ value, mode }: FilterTextChangeValue) => {
    updateSearchParamsByKey({
      executionTime: value,
      executionTimeFilterMode: mode,
    });
  };

  const facetCtx = useMemo(
    () => ({ startDate, endDate, workflowType }),
    [startDate, endDate, workflowType]
  );

  // Table headers
  const tableHeaders: TableHeaderType<GetSnowflakeJobItem>[] = [
    {
      id: 1,
      label: getWorkflowIdLabel(workflowType),
      uid: "workflow_id",
      render: (item: GetSnowflakeJobItem) => (
        <WorkflowIdCell item={item} workflowType={workflowType} />
      ),
      filter: {
        id: "workflows",
        label: "Workflows",
        searchKey: "workflows",
        value: workflowFilter,
        ...workflowIdsFilter(facetCtx),
      },
    },
    {
      id: 2,
      label: "Total Cost",
      uid: "query_cost",
      render: (item: GetSnowflakeJobItem) => <div>{formatCost(item.query_cost, currency)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
      filter: {
        id: "query_cost",
        filterType: FilterType.TEXT,
        value: costFilter ?? undefined,
        placeHolder: `Specify cost in ${currencySymbol}`,
        onChange: handleCostFilterChange,
        label: "Total Cost",
        filterModeSearchKey: "costFilterMode",
        filterMode: { value: costFilterMode },
      },
    },
    {
      id: 3,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      render: (item: GetSnowflakeJobItem) => <div>{formatBytes(item.bytes_scanned)}</div>,
    },
    {
      id: 4,
      label: "Execution Time",
      uid: "execution_time",
      render: (item: GetSnowflakeJobItem) => <div>{formatMilliseconds(item.execution_time)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
      filter: {
        id: "executionTime",
        filterType: FilterType.TEXT,
        value: executionTimeFilter ?? undefined,
        placeHolder: "Specify time in seconds",
        onChange: handleExecutionTimeFilterChange,
        label: "Execution Time",
        filterModeSearchKey: "executionTimeFilterMode",
        filterMode: { value: executionTimeFilterMode },
      },
    },
    {
      id: 5,
      label: "Runs",
      uid: "runs",
      render: (item: GetSnowflakeJobItem) => <div>{item.runs}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
    },
    {
      id: 6,
      label: "Warehouses",
      uid: "warehouses",
      render: (item: GetSnowflakeJobItem) => (
        <div className={classNames("al-break-words", "lines-2")}>
          {item.warehouse_names.join(", ")}
        </div>
      ),
      filter: {
        id: "warehouses",
        label: "Warehouses",
        searchKey: "warehouses",
        filterType: FilterType.DROPDOWN,
        value: warehouseFilter,
        options:
          filters?.warehouses.map(t => ({
            label: t,
            value: t,
          })) ?? [],
      },
    },
    {
      id: 7,
      label: getWorkflowTerminology(workflowType, "Projects"),
      uid: "teams",
      render: (item: GetSnowflakeJobItem) => <ColorInsightTags tags={item.teams} showMore />,
      filter: {
        id: "teams",
        label: getWorkflowTerminology(workflowType, "Projects"),
        searchKey: "teams",
        value: teamsFilter,
        ...teamsFilterConfig(facetCtx, filters?.teams),
      },
    },
    {
      id: 8,
      label: getWorkflowTerminology(workflowType, "Apps"),
      uid: "apps",
      render: (item: GetSnowflakeJobItem) => {
        return <ColorInsightTags tags={item.apps.filter(Boolean)} showMore />;
      },
      filter: {
        id: "apps",
        label: getWorkflowTerminology(workflowType, "Apps"),
        searchKey: "apps",
        value: appsFilter,
        ...appsFilterConfig(facetCtx, filters?.apps),
      },
    },
  ];

  const graphDataSubset = useMemo(() => {
    if (!graphData) {
      return [];
    }

    const result = [];
    for (const item of graphData) {
      const resultItem: { date?: string } & Record<string, number> = {};
      resultItem["date"] = item.date;

      for (const key in item) {
        if (key === "date") {
          continue;
        }

        const nestedObject = item[key];
        const identifier =
          nestedObject?.dbt_project_name === null ? key : nestedObject?.dbt_project_name;

        if (!identifier) {
          continue;
        }

        if (selectedLegends.includes(identifier)) {
          resultItem[identifier] = nestedObject?.cost_value || 0;

          if (result.length > 0) {
            const lastItem = result[result.length - 1];
            if (lastItem[identifier] > 0) {
              resultItem[getPercentKey(identifier)] =
                (nestedObject?.cost_value - lastItem[identifier]) / lastItem[identifier];
            }
          }
        } else {
          resultItem[OTHER] = resultItem[OTHER] || 0;
          resultItem[OTHER] += nestedObject?.cost_value || 0;

          if (result.length > 0) {
            const lastItem = result[result.length - 1];
            if (lastItem[OTHER] > 0) {
              resultItem[getPercentKey(OTHER)] =
                (nestedObject?.cost_value - lastItem[OTHER]) / lastItem[OTHER];
            }
          }
        }
      }

      result.push(resultItem);
    }

    return result;
  }, [graphData, selectedLegends]);

  const colorsMap = useMemo(() => {
    return createColorMapFromObjectKeys(graphData || [], {
      reservedMappings: { [OTHER]: "#5B9FFF" },
      excludeKeys: ["date"],
    });
  }, [graphData]);

  // Only show full-page loader on initial load, not on refetches (e.g., when changing aggregateBy)
  // This prevents the "page refresh" effect when switching group-by options
  if (isLoading || isFiltersFetching || isGraphLoading) {
    return <RelativeComponentLoader componentHeight={50} label="Loading..." />;
  }

  return (
    <Card className="al-p-4">
      <Stack direction="column">
        <Stack align="center">
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Typography variant="page-heading">Jobs</Typography>
            <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.WORKLOADS} />
          </Stack>
          <div className="spacer" />
          <DateRange
            startDate={startDate}
            endDate={endDate}
            availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
            onDateRangeSelect={onUpdate}
          />
        </Stack>
        <Stack direction="column" className="al-p-3 al-border al-rounded-md">
          <Stack align="center">
            <TotalCostHeader
              queryKey={graphSummaryQueryKey}
              queryFn={getSnowflakeJobGraphSummary}
              params={graphParams}
              title={`Total ${getWorkflowTerminology(workflowType, "Jobs Cost")}`}
            />
            <div className="spacer" />
            <GroupByDropdown
              value={aggregateBy}
              onChange={setAggregateBy}
              options={groupByOptions}
            />

            {isCustomTagSelected && entityValues.length > 0 && (
              <Combobox
                options={
                  (allLegends.length > 10 ? [OTHER] : [])
                    .concat(entityValues)
                    .map(v => ({ label: v, value: v })) as ComboboxOption[]
                }
                value={selectedLegends}
                onChange={value => {
                  if (Array.isArray(value)) {
                    setSelectedLegends(value);
                  }
                }}
                placeholder="Show: All"
                header="Show"
                searchPlaceholder="Search entities..."
                multiSelect
                showApplyButton
                showClearButton
                popoverContentProps={{ className: "al-min-w-[200px]" }}
                {...{
                  onLoadMore: handleEntityLoadMore,
                  hasMore: Boolean(hasMoreEntities),
                  onSearch: handleEntitySearch,
                  onPageReset: handleEntityPageReset,
                  currentPage:
                    groupByEntitiesData?.pages?.[groupByEntitiesData.pages.length - 1]?.page ?? 1,
                }}
              />
            )}

            {(isProjectSelected || isAppSelected) && builtInFilterOptions.length > 0 && (
              <Combobox
                options={builtInFilterOptions as ComboboxOption[]}
                value={selectedBuiltInFilters}
                onChange={value => {
                  if (Array.isArray(value)) {
                    setSelectedBuiltInFilters(value);
                  }
                }}
                placeholder={`Filter: All ${isProjectSelected ? "Projects" : "Apps"}`}
                header={isProjectSelected ? "Filter Projects" : "Filter Apps"}
                searchPlaceholder={`Search ${isProjectSelected ? "projects" : "apps"}...`}
                multiSelect
                showApplyButton
                showClearButton
                popoverContentProps={{ className: "al-min-w-[200px]" }}
              />
            )}
          </Stack>
          {isGraphLoading ? (
            <RelativeComponentLoader label="Loading..." />
          ) : graphDataSubset.length > 0 ? (
            <div className="al-border al-rounded-md al-p-4 al-pb-10">
              <Chart
                title="Cost"
                xAxisDataKey="date"
                yAxisLabel={{ value: "Cost" }}
                xAxisLabelFormatter={value => dayjs(value).format("MMM-DD")}
                yAxisLabelFormatter={value => formatCost(value, currency)}
                type={ChartTypes.BarChart}
                data={graphDataSubset}
                hideFullscreenButton
                tooltipProps={{
                  content: (props: TooltipProps<number, string>) => (
                    <CostChartTooltip
                      {...props}
                      aggregateBy={AGGREGATE_BY_OPTIONS.find(i => i.value === aggregateBy)?.label!}
                    />
                  ),
                }}
                chartProps={{}}
              >
                {({ barSize }) => {
                  return (
                    <>
                      {selectedLegends?.map((d, i) => (
                        <Bar
                          key={d}
                          barSize={barSize}
                          dataKey={d}
                          fill={colorsMap[d]}
                          stackId="single"
                          shape={(props: unknown) => {
                            (props as { radius: number[] }).radius =
                              i === selectedLegends.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0];
                            return <Rectangle {...(props as Record<string, unknown>)} />;
                          }}
                        />
                      ))}
                    </>
                  );
                }}
              </Chart>
            </div>
          ) : (
            <div className="al-w-full text-center">No data is present</div>
          )}
        </Stack>
        {isLoading ? (
          <RelativeComponentLoader label="Loading..." />
        ) : (
          <Stack direction="column" className={"al-mt-4"}>
            <Stack align="center">
              <Typography variant="page-heading">
                {getWorkflowTerminology(workflowType, "Jobs List")}
              </Typography>
            </Stack>
            <FilterTagWrapper
              applicableFor={undefined}
              filters={{
                costFilter: {
                  label: "Cost",
                  filterStr: getFilterStringByMode(costFilter, costFilterMode) || [],
                  searchKey: "cost",
                  onclose: () =>
                    deleteSearchParamsByKeyValue({
                      query_cost: undefined,
                      costFilterMode: undefined,
                    }),
                },
                executionTimeFilter: {
                  label: "Execution time",
                  filterStr:
                    getFilterStringByMode(executionTimeFilter, executionTimeFilterMode) || [],
                  searchKey: "executionTime",
                  onclose: () =>
                    deleteSearchParamsByKeyValue({
                      executionTime: undefined,
                      executionTimeFilterMode: undefined,
                    }),
                },
                warehouseFilter: {
                  label: "Warehouses",
                  filterStr: warehouseFilter,
                  searchKey: "warehouses",
                  onclose: (value?: string) => deleteSearchParamsByKeyValue({ warehouses: value }),
                },
                workflowFilter: {
                  label: "Workload",
                  filterStr: workflowFilter,
                  searchKey: "workflows",
                  onclose: (value?: string) => deleteSearchParamsByKeyValue({ workflows: value }),
                },
                teamsFilter: {
                  label: getWorkflowTerminology(workflowType, "Projects"),
                  filterStr: teamsFilter,
                  searchKey: "teams",
                  onclose: (value?: string) => deleteSearchParamsByKeyValue({ teams: value }),
                },
                appsFilter: {
                  label: getWorkflowTerminology(workflowType, "Apps"),
                  filterStr: appsFilter,
                  searchKey: "apps",
                  onclose: (value?: string) => deleteSearchParamsByKeyValue({ apps: value }),
                },
              }}
            />
            <DataTable
              tableClass="mt-0"
              header={tableHeaders}
              // @ts-ignore
              data={data}
              idKey={(item: GetSnowflakeJobItem) => item.workflow_id}
              gridTemplateColumns="2fr 1fr 1fr 1fr 1fr 2fr 1fr 1fr"
              defaultSort={{ sortParamValue: "query_cost", order: "desc" }}
            />

            <Paginate
              itemCount={data?.total || 0}
              numPages={data?.pages || 0}
              page={page}
              pageSize={pageSize}
              onPageClick={setPage}
              onPageSizeChange={updatePageSize}
              showPageSize
            />
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

const RunTask = () => {
  const { workflowId, runId } = useParams();
  const { currency, enableWarehouseSizeRecommendation } = useAppState();
  const { startDate, endDate } = useDateFromSearchParams();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const location = useLocation();
  const { searchParams } = useFiltersBySearchParams();

  const sortAttribute = searchParams.get("sortAttribute") || "query_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");

  // Determine workflow type from the current path
  const workflowType = location.pathname.includes("/tableau-jobs")
    ? WorkflowType.CUSTOM
    : WorkflowType.CUSTOM;

  const { data, isLoading } = useQuery({
    queryKey: [
      "snowflake_jobs",
      "runs",
      "tasks",
      workflowId,
      runId,
      startDate,
      endDate,
      page,
      pageSize,
      sortAttribute,
      sortOrder,
    ],
    queryFn: () =>
      getSnowflakeJobRunTasks(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        page: page + 1,
        size: pageSize,
        run_id: runId,
        sort_key: sortAttribute,
        sort_order: sortOrder,
      }),
    enabled: Boolean(workflowId && runId),
  });

  const tableHeaders = [
    {
      id: 1,
      label: "Task ID",
      uid: "task_id",
      render: (item: GetSnowflakeJobTaskItem) => (
        <Link className="al-text-brand al-break-all cursor-pointer" to={`/query/${item.query_id}`}>
          <QueryTextWithHover queryText={item.task_id || item.query_id} queryHash={item.hash} />
        </Link>
      ),
    },
    {
      id: 2,
      label: "Query Cost",
      uid: "query_cost",
      sort: {
        sortSearchKey: "sortAttribute",
      },
      render: (item: GetSnowflakeJobTaskItem) => (
        // @ts-ignore
        <div>{formatCost(item.query_cost, currency)}</div>
      ),
    },
    {
      id: 3,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      sort: {
        sortSearchKey: "sortAttribute",
      },
      render: (item: GetSnowflakeJobTaskItem) => (
        // @ts-ignore
        <div>{formatBytes(item.bytes_scanned)}</div>
      ),
    },
    {
      id: 4,
      label: "Execution Time",
      uid: "execution_time",
      sort: {
        sortSearchKey: "sortAttribute",
      },
      render: (item: GetSnowflakeJobTaskItem) => (
        <div>{formatMilliseconds(item.execution_time)}</div>
      ),
    },
    {
      id: 5,
      label: "Warehouse",
      uid: "warehouse",
      render: (item: GetSnowflakeJobTaskItem) => <div>{item.warehouse_name}</div>,
    },
    {
      id: 6,
      label: "Timestamp",
      uid: "start_time",
      sort: {
        sortSearchKey: "sortAttribute",
      },
      render: (item: GetSnowflakeJobTaskItem) => (
        <div>{dayjs(item.start_time).format("YYYY-MM-DD hh:mm a")}</div>
      ),
    },
    ...(enableWarehouseSizeRecommendation
      ? [
          {
            id: 7,
            label: "Optimal Warehouse Size",
            uid: "optimal_warehouse_size",
            render: (item: GetSnowflakeJobTaskItem) => (
              <span>{item.optimal_warehouse_size ?? ""}</span>
            ),
          },
        ]
      : []),
    {
      id: 8,
      label: "Status",
      uid: "execution_status",
      render: (item: GetSnowflakeJobTaskItem) => <ExecutionStatus {...item} />,
    },
  ];
  if (!(workflowId && runId)) {
    return <Navigate to={getWorkflowNavigationPrefix(workflowType)} />;
  }
  if (isLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }
  if (!data) {
    return <Navigate to={getWorkflowNavigationPrefix(workflowType)} />;
  }
  return (
    <Stack direction="column">
      <DataTable
        header={tableHeaders}
        // @ts-ignore
        data={data}
        idKey={(item: GetSnowflakeJobTaskItem) => item.task_id}
        gridTemplateColumns="2fr 1fr 1fr 1fr 1.5fr 1.5fr 0.8fr"
        defaultSort={{ sortParamValue: "query_cost", order: "desc" }}
      />
      <Paginate
        itemCount={data?.total || 0}
        numPages={Math.ceil((data?.total || 0) / pageSize)}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
      />
    </Stack>
  );
};

const RunSummary = ({ run }: { run: GetSnowflakeJobRunItem }) => {
  const { currency } = useAppState();
  return (
    <Stack>
      {[
        {
          label: "Run Cost",
          // @ts-ignore
          value: formatCost(run.query_cost, currency),
        },
        {
          label: "Exec. Time",
          value: formatMilliseconds(run.execution_time),
        },
      ].map(item => (
        <Stack key={item.label} className="gap-xs">
          <div className="al-text-light-gray">{item.label}:</div>
          <div className="text-black font-semibold">{item.value}</div>
        </Stack>
      ))}
    </Stack>
  );
};

const CompareRowItem = ({
  run,
  diff,
}: {
  run: RunCompareDiff["run1"];
  diff?: {
    execution_time: number;
    query_cost: number;
  };
}) => {
  const { currency } = useAppState();
  const warehouseSize = WarehouseSizeMap[run.warehouse_size!];

  return (
    <Stack direction="column" className="gap-0">
      <Stack>
        <div className="al-text-light-gray">Cost:</div>
        <div>{run.query_cost ? formatCost(run.query_cost, currency) : "-"}</div>
        {diff?.query_cost && <PercentChange percentChange={diff?.query_cost} />}
      </Stack>
      <Stack>
        <div className="al-text-light-gray">Exec. Time:</div>
        <div>{run.execution_time ? formatMilliseconds(run.execution_time) : "-"}</div>
        {diff?.execution_time && <PercentChange percentChange={diff?.execution_time} />}
      </Stack>
      <Stack>
        <div className="al-text-light-gray">Warehouse:</div>
        <div className="al-text-brand">
          {run.warehouse_name
            ? run.warehouse_name + (warehouseSize ? ` (${warehouseSize})` : "")
            : "-"}
        </div>
      </Stack>
      <Stack>
        <div className="al-text-light-gray">Status:</div>
        {run.execution_status ? (
          <ExecutionStatus
            execution_status={run.execution_status}
            error_message={run.error_message}
          />
        ) : (
          "-"
        )}
      </Stack>
    </Stack>
  );
};

const CompareRuns = () => {
  const { workflowId } = useParams();
  const [run1, setRun1] = useState("");
  const [run2, setRun2] = useState("");
  const { startDate, endDate } = useDateFromSearchParams();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const [openedAccordion, setOpenedAccordion] = useState("");

  useEffect(() => {
    if (workflowId) {
      QueriesTracking.runsCompared(workflowId);
    }
  }, [workflowId]);

  const { data, isLoading } = useQuery({
    queryKey: ["snowflake_jobs", "runs", workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobRuns(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
    enabled: Boolean(workflowId),
  });
  const runOptions = data?.items.map(item => ({
    label: item.run_id,
    value: item.run_id,
  }));
  const _run1 = data?.items?.find(item => item.run_id === run1);
  const _run2 = data?.items?.find(item => item.run_id === run2);
  const { data: compareData, isLoading: isCompareLoading } = useQuery({
    queryKey: ["snowflake_jobs", "compare", workflowId, run1, run2],
    queryFn: () =>
      compareSnowflakeJobRuns({
        workflow_id: workflowId!,
        run1_id: run1,
        run2_id: run2,
      }),
    enabled: Boolean(workflowId && _run1 && _run2),
  });
  if (isLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }
  return (
    <Stack direction="column">
      <Card className={"!al-bg-bg-brand al-p-4"}>
        <Stack justify={"center"}>
          <Stack direction="column" className="al-w-full">
            <Label>Run 1</Label>
            <NativeSelect
              placeholder={"Select a run"}
              className={"al-w-full !al-bg-white"}
              variant={"outline"}
              options={runOptions ?? []}
              value={run1}
              onChange={setRun1}
              buttonProps={{ "aria-label": "Select Run 1" }}
            />
            {_run1 ? <RunSummary run={_run1} /> : null}
          </Stack>
          <Button
            className={"al-mt-5"}
            variant={"ghost"}
            aria-label="Swap run selections"
            onClick={() => {
              setRun1(run2);
              setRun2(run1);
            }}
          >
            <IconArrowsLeftRight />
          </Button>
          <Stack direction="column" className="al-w-full">
            <Label>Run 2</Label>
            <NativeSelect
              placeholder={"Select a run"}
              className={"al-w-full !al-bg-white"}
              variant={"outline"}
              options={runOptions ?? []}
              value={run2}
              onChange={setRun2}
              buttonProps={{ "aria-label": "Select Run 2" }}
            />
            {_run2 ? <RunSummary run={_run2} /> : null}
          </Stack>
        </Stack>
      </Card>
      {isCompareLoading ? (
        <RelativeComponentLoader label="Loading..." />
      ) : compareData ? (
        <>
          <DataTable
            idKey={item => item.task_id}
            tableRowClass="al-items-start"
            header={[
              {
                id: 1,
                label: "Task",
                uid: "task_id",
                render: (item: RunCompareDiff) => (
                  <Stack direction="column" className="gap-xs">
                    <div className="al-break-all">{item.task_id}</div>
                    {item.type === "added" && <ColorTag color="red" label="Task Added" />}
                    {item.type === "removed" && <ColorTag color="green" label="Task Removed" />}
                    {item.type === "updated" && (
                      <>
                        {item.run1.query_hash !== item.run2.query_hash && (
                          <ColorTag color="blue" label="Code Changed" />
                        )}
                        {item.run1.warehouse_name !== item.run2.warehouse_name && (
                          <ColorTag color="yellow" label="Warehouse Changed" />
                        )}
                      </>
                    )}
                    {(item.run1.execution_status === "FAIL" ||
                      item.run2.execution_status === "FAIL") && (
                      <ColorTag color="orange" label="Task Failed" />
                    )}
                  </Stack>
                ),
              },
              {
                id: 2,
                label: (
                  <Stack direction="column">
                    <Label>Run 1</Label>
                    <NativeSelect
                      className={"al-min-w-36"}
                      variant={"outline"}
                      options={runOptions ?? []}
                      value={run1}
                      onChange={setRun1}
                    />
                    {_run1 ? <RunSummary run={_run1} /> : <div />}
                  </Stack>
                ),
                uid: "run1",
                render: (item: RunCompareDiff) => <CompareRowItem run={item.run1} />,
              },
              {
                id: 3,
                label: (
                  <Stack direction="column">
                    <Label>Run 2</Label>
                    <NativeSelect
                      className={"al-min-w-36"}
                      variant={"outline"}
                      options={runOptions ?? []}
                      value={run2}
                      onChange={setRun2}
                    />
                    {_run2 ? <RunSummary run={_run2} /> : <div />}
                  </Stack>
                ),
                uid: "run2",
                render: (item: RunCompareDiff) => (
                  <Stack align="center">
                    <div className="flex-grow">
                      <CompareRowItem
                        run={item.run2}
                        diff={
                          item.run2.execution_time &&
                          item.run2.query_cost &&
                          item.run1.execution_time &&
                          item.run1.query_cost
                            ? {
                                execution_time:
                                  (item.run2.execution_time - item.run1.execution_time) /
                                  item.run1.execution_time,
                                query_cost:
                                  (item.run2.query_cost - item.run1.query_cost) /
                                  item.run1.query_cost,
                              }
                            : undefined
                        }
                      />
                    </div>
                    {item.run1.query_hash !== item.run2.query_hash ? (
                      <Button
                        size="sm"
                        onClick={() =>
                          setOpenedAccordion(prev => (prev === item.task_id ? "" : item.task_id))
                        }
                        color={"primary"}
                        className={openedAccordion === item.task_id ? styles.secondary_btn : ""}
                      >
                        Details
                      </Button>
                    ) : null}
                  </Stack>
                ),
              },
            ]}
            data={{
              items: (compareData || []).slice(page * pageSize, (page + 1) * pageSize),
            }}
            gridTemplateColumns="1fr 1.5fr 1.5fr"
            openedAccordion={openedAccordion}
            accordionComponent={(item: RunCompareDiff) => <CompareAccordionContent item={item} />}
          />
          <Paginate
            itemCount={compareData?.length || 0}
            numPages={Math.ceil((compareData?.length || 0) / pageSize)}
            page={page}
            pageSize={pageSize}
            onPageClick={setPage}
            onPageSizeChange={updatePageSize}
            showPageSize
          />
        </>
      ) : null}
    </Stack>
  );
};

/**
 * TODO: DELETE THIS COMPONENT WHEN FEATURE FLAG IS FULLY ROLLED OUT
 * This component is only used in legacy mode (when refactoredJobDetails flag is false).
 * In the new implementation, this data is shown in the "Tasks" tab of JobDetails component.
 * Lines to remove: 1108-1205 (this entire component including this comment)
 * Also remove: The conditional Drawer rendering in SnowflakeJob component (lines with !useRefactoredJobDetails)
 */
const WorkloadDefinition = ({ workflowId }: { workflowId: string }) => {
  const { currency } = useAppState();
  const { page, pageSize, setPage, updatePageSize } = usePaginate();
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { data, isLoading } = useQuery({
    queryKey: ["snowflake_jobs", "definition", workflowId],
    queryFn: () =>
      getSnowflakeJobDefinition(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
  });
  if (isLoading) {
    return <RelativeComponentLoader label="Loading" />;
  }
  return (
    <Stack direction="column">
      <Stack>
        <div className="fs-4">Latest Job Definition</div>
        <div className="spacer" />
        <DateRange
          startDate={startDate}
          endDate={endDate}
          availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
          disableCalendarSelection
          onDateRangeSelect={onUpdate}
        />
      </Stack>
      <Stack>
        <Stack gap="xxs">
          <div className="al-text-light-gray">Run ID:</div>
          <div className="al-text-brand">{data?.run_id}</div>
        </Stack>
        <Stack gap="xxs">
          <div className="al-text-light-gray">Latest timestamp:</div>
          <div className="al-text-brand">
            {dayjs(data?.timestamp).format("DD MMM YYYY hh:mm:ss A")}
          </div>
        </Stack>
      </Stack>
      <DataTable
        header={[
          {
            id: 1,
            label: "Task ID",
            uid: "task_id",
            render: (item: SnowflakeJobDefinition) => (
              <div className="al-text-brand al-break-all">{item.task_id}</div>
            ),
          },
          {
            id: 2,
            label: "Avg. Cost",
            uid: "query_cost",
            render: (item: SnowflakeJobDefinition) => (
              <div>{formatCost(item.query_cost, currency)}</div>
            ),
          },
          {
            id: 3,
            label: "Avg. Time",
            uid: "execution_time",
            render: (item: SnowflakeJobDefinition) => (
              <div>{formatMilliseconds(item.execution_time)}</div>
            ),
          },
          {
            id: 4,
            label: "Runs",
            uid: "run_count",
            render: (item: SnowflakeJobDefinition) => <div>{item.run_count}</div>,
          },
          {
            id: 5,
            label: "Status",
            uid: "status",
            render: (item: SnowflakeJobDefinition) => <ExecutionStatusCount {...item} />,
          },
        ]}
        data={{
          // @ts-ignore
          items: data?.definition.slice(page * pageSize, (page + 1) * pageSize),
        }}
        idKey={(item: SnowflakeJobDefinition) => item.task_id}
        gridTemplateColumns="3fr 1fr 1fr 0.8fr 1fr"
      />
      <Paginate
        itemCount={data?.definition.length || 0}
        numPages={Math.ceil((data?.definition.length || 0) / pageSize)}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
      />
    </Stack>
  );
};

const SnowflakeJob = () => {
  const { workflowId, runId } = useParams();
  const location = useLocation();
  const ref = useRef<DrawerRef | null>(null);
  const { startDate, endDate, onUpdate, navigateTo } = useDateFromSearchParams();
  const { enableWarehouseSizeRecommendation } = useAppState();

  // Determine workflow type from the current path
  const workflowType = location.pathname.includes("/tableau-jobs")
    ? WorkflowType.TABLEAU
    : WorkflowType.CUSTOM;

  useEffect(() => {
    if (workflowId) {
      if (runId) {
        QueriesTracking.runDetailViewed(workflowId, runId);
      } else {
        QueriesTracking.jobDetailViewed(workflowId, workflowType);
      }
    }
  }, [workflowId, runId, workflowType]);

  const { data: summaryData } = useQuery({
    queryKey: ["snowflake_job", workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobsSummary(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workflow_type: workflowType,
      }),
  });

  return (
    <Card className="al-p-4">
      <Stack direction="column" className="gap-sm">
        <Stack align="center" justify={"between"} className={"al-w-full"}>
          <Stack align={"center"}>
            <Button
              variant={"ghost"}
              className="icon-sm cursor-pointer"
              aria-label="Go back"
              onClick={() => {
                const parts = location.pathname.split("/").filter(Boolean);
                const prefix = getWorkflowNavigationPrefix(workflowType);
                if (parts.length > 3) {
                  navigateTo(`${prefix}/${encodeURIComponent(workflowId!)}`);
                } else {
                  navigateTo(prefix);
                }
              }}
            >
              <IconArrowLeft className="al-h-4 al-w-4" />
            </Button>
            <WorkflowTitle
              workflowType={workflowType}
              workflowId={workflowId}
              runId={runId}
              tableauDashboardName={summaryData?.tableau_dashboard_name ?? undefined}
            />
          </Stack>
          <Stack align={"center"}>
            {!enableWarehouseSizeRecommendation && (
              <Drawer
                buttonText="Definition"
                ref={ref}
                style={{
                  maxWidth: "none",
                }}
              >
                <WorkloadDefinition workflowId={workflowId!} />
              </Drawer>
            )}
            <DateRange
              startDate={startDate}
              endDate={endDate}
              availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
              disableCalendarSelection
              onDateRangeSelect={onUpdate}
            />
          </Stack>
        </Stack>
        <div />
        <Outlet />
      </Stack>
    </Card>
  );
};

export { SnowflakeJobList, SnowflakeJob, RunTask, CompareRuns };
